import React, {useEffect, useMemo, useRef} from "react";
import {Box, IconButton, Stack} from "@mui/material";
import {useState} from "react";
import {useParams} from "react-router-dom";
import axios from "../../axios/axios";
import useWebSocket from "../../hooks/useWebSocket";
import useAudioQueue from "./useAudioQueue";
import {Pause, PlayArrow, SkipNextOutlined, SkipPreviousOutlined} from "@mui/icons-material";
import {MessageBox} from "./MessageBox";

export const ChatPreview = React.memo(() => {
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const {chatId: id} = useParams();
  const { play, pause, selectMessageById, handleNext, handlePrev, isPlaying, currentIndex, hasPrev, hasNext, canPlay, playingMessage } = useAudioQueue(messages);
  const [updatedValue, state] = useWebSocket(`/topic/session/`, id);
  const bottomRef = useRef();

  useEffect(() => {
    if (id) {
      fetchMessages();
      axios.get(`/api/conversation/${id}`)
        .then(response => {
          setSession(response?.data);
        })
        .catch((error) => console.log(error?.message))
    } else {
      setMessages([]);
    }
  }, [id])

  const fetchMessages = () => {
    axios.get(`/api/conversation/${id}/messages`)
      .then(response => {
        setMessages(response.data);
      })
      .catch(error => {
        console.log(error);
      })
  }

  useEffect(() => {
    if (messages.length && bottomRef.current) {
      bottomRef.current.scrollIntoView({behavior: 'smooth'});
    }
  }, [messages?.length, bottomRef.current])

  useEffect(() => {
    if (updatedValue && +id === +updatedValue?.session) {
      const newMessage = updatedValue.message;
      setMessages(prevState => {
        const items = [...prevState];
        const index = items.findIndex(i => i.id === newMessage.id);
        if (isFinite(index) && index >= 0) {
          items[index] = newMessage;
        } else {
          items.push(newMessage);
        }
        return items.sort((p1, p2) => (p1.dateCreated > p2.dateCreated) ? 1 : (p1.dateCreated < p2.dateCreated) ? -1 : 0)
      });

    }
  }, [updatedValue])


  const filteredMessages = useMemo(() => {
    return messages?.filter(message => message?.text) || []
  }, [messages])

  return (
    <>
      <Stack direction={"row"} gap={1} sx={{position: 'relative', maxHeight: "calc(100dvh - 0px)", height: "100%"}}>
        <Stack direction={"column"} sx={{
          maxHeight: "calc(100dvh - 1px)", height: "100dvh",
          flex: 1,
        }}>
          <Box
            sx={{
              zIndex: -1,
              position: "absolute",
              top: "50%", left: "50%",
              transform: "translate(-30%, -100%)",
              backdropFilter: "blur(10px)",
            }}
          >
            <img src={"/logo.svg"} alt={"Logo"} width={"60%"} height={"auto"}
                 style={{filter: "brightness(130%) blur(10px)"}}/>
          </Box>
          <Box sx={{flex: 1, p: 2, overflowY: 'auto', zIndex: 10}}>
            <Stack direction={"column"} maxWidth={"md"} sx={{mx: 'auto'}} gap={1}>
              {
                filteredMessages?.map((message, index) => {
                  const prev = filteredMessages[index - 1] || null;

                  return (
                    <MessageBox
                      key={message?.id || index}
                      message={message} prevMessage={prev}
                      selectedMessage={selectedMessage || playingMessage}
                      onSelectMessage={() => {
                        setSelectedMessage(prev => (prev?.id === message?.id ? null : message))
                        if (message?.id) {
                          selectMessageById(message?.id);
                        }
                      }}
                    />
                  )
                })
              }
              <div ref={bottomRef}/>
            </Stack>
          </Box>

          <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} gap={2} sx={{pt: 2, pb: 1}}>
            <IconButton
              disabled={!hasPrev}
              onClick={handlePrev}
            >
              <SkipPreviousOutlined/>
            </IconButton>
            <IconButton
              disabled={!canPlay}
              onClick={() => isPlaying ? pause() : play()}
            >
              {isPlaying ? <Pause/> : <PlayArrow />}
            </IconButton>
            <IconButton
              disabled={!hasNext}
              onClick={handleNext}
            >
              <SkipNextOutlined/>
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    </>
  )
})